const axes = ['x', 'y', 'z'];
const finite = (point) => point && axes.every((axis) => Number.isFinite(point[axis]));
const add = (a, b, t = 1) => ({ x: a.x + b.x * t, y: a.y + b.y * t, z: a.z + b.z * t });
const sub = (a, b) => add(a, b, -1);
const dot = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
const cross = (a, b) => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x,
});
const cache = new WeakMap();
const meshCache = new WeakMap();
function geometry(triangle) {
  if (!triangle || typeof triangle !== 'object') return null;
  if (cache.has(triangle)) return cache.get(triangle);
  const points = [triangle.a, triangle.b, triangle.c];
  if (!points.every(finite)) return null;
  const normal = cross(sub(triangle.b, triangle.a), sub(triangle.c, triangle.a));
  const area2 = dot(normal, normal);
  if (!Number.isFinite(area2) || area2 <= 1e-16) return null;
  const bounds = axes.map((axis) => [
    Math.min(...points.map((point) => point[axis])),
    Math.max(...points.map((point) => point[axis])),
  ]);
  if (Object.isFrozen(triangle) && points.every(Object.isFrozen)) cache.set(triangle, bounds);
  return bounds;
}

// Voronoi regions provide the nearest point on the actual finite triangle,
// including edges and corners rather than its infinitely extended plane.
function nearest(point, { a, b, c }) {
  const ab = sub(b, a),
    ac = sub(c, a),
    ap = sub(point, a);
  const d1 = dot(ab, ap),
    d2 = dot(ac, ap);
  if (d1 <= 0 && d2 <= 0) return a;
  const bp = sub(point, b),
    d3 = dot(ab, bp),
    d4 = dot(ac, bp);
  if (d3 >= 0 && d4 <= d3) return b;
  const vc = d1 * d4 - d3 * d2;
  if (vc <= 0 && d1 >= 0 && d3 <= 0) return add(a, ab, d1 / (d1 - d3));
  const cp = sub(point, c),
    d5 = dot(ab, cp),
    d6 = dot(ac, cp);
  if (d6 >= 0 && d5 <= d6) return c;
  const vb = d5 * d2 - d1 * d6;
  if (vb <= 0 && d2 >= 0 && d6 <= 0) return add(a, ac, d2 / (d2 - d6));
  const va = d3 * d6 - d5 * d4;
  if (va <= 0 && d4 - d3 >= 0 && d5 - d6 >= 0)
    return add(b, sub(c, b), (d4 - d3) / (d4 - d3 + d5 - d6));
  const denominator = 1 / (va + vb + vc);
  return add(add(a, ab, vb * denominator), ac, vc * denominator);
}

/** Distance in metres to a visible finite hull-local triangle. Invalid or
 * degenerate geometry returns Infinity and cannot introduce an invisible wall. */
export function skyLeviathanTriangleDistance(point, triangle) {
  if (!finite(point) || !geometry(triangle)) return Infinity;
  const delta = sub(point, nearest(point, triangle));
  return Math.hypot(delta.x, delta.y, delta.z);
}

function meshIndex(triangles) {
  // Accepted arrays and their facets are immutable. JavaScriptCore scans array
  // descriptors in Object.isFrozen, so repeating that admission check on a
  // 19k-facet hull made every cached ray linear before it even reached the BVH.
  if (meshCache.has(triangles)) return meshCache.get(triangles);
  if (
    !Array.isArray(triangles) ||
    !Object.isFrozen(triangles) ||
    !triangles.length ||
    triangles.length > 100000
  )
    return null;
  const entries = [];
  for (const triangle of triangles) {
    const bounds = geometry(triangle);
    if (
      !bounds ||
      !Object.isFrozen(triangle) ||
      ![triangle.a, triangle.b, triangle.c].every(Object.isFrozen)
    )
      return null;
    entries.push({ triangle, bounds });
  }
  function build(items) {
    const bounds = axes.map(() => [Infinity, -Infinity]);
    for (const item of items)
      for (let axis = 0; axis < 3; axis++) {
        bounds[axis][0] = Math.min(bounds[axis][0], item.bounds[axis][0]);
        bounds[axis][1] = Math.max(bounds[axis][1], item.bounds[axis][1]);
      }
    if (items.length <= 8) return { bounds, items };
    const axis = [0, 1, 2].sort(
      (a, b) => bounds[b][1] - bounds[b][0] - (bounds[a][1] - bounds[a][0]),
    )[0];
    items.sort(
      (a, b) => a.bounds[axis][0] + a.bounds[axis][1] - b.bounds[axis][0] - b.bounds[axis][1],
    );
    const middle = Math.floor(items.length / 2);
    return { bounds, left: build(items.slice(0, middle)), right: build(items.slice(middle)) };
  }
  const index = build(entries);
  meshCache.set(triangles, index);
  return index;
}

function boundsEntry(from, to, bounds, radius, maximum) {
  let enter = 0,
    exit = maximum;
  for (let index = 0; index < 3; index++) {
    const axis = axes[index],
      velocity = to[axis] - from[axis];
    const low = bounds[index][0] - radius,
      high = bounds[index][1] + radius;
    if (Math.abs(velocity) < 1e-12) {
      if (from[axis] < low || from[axis] > high) return null;
    } else {
      const a = (low - from[axis]) / velocity,
        b = (high - from[axis]) / velocity;
      enter = Math.max(enter, Math.min(a, b));
      exit = Math.min(exit, Math.max(a, b));
      if (enter > exit) return null;
    }
  }
  return enter;
}

/** Readiness preparation builds the immutable surface index before controls
 * start. Returns false for malformed/mutable data; no ray or gameplay advances. */
export function prepareSkyLeviathanMesh(triangles) {
  return Boolean(meshIndex(triangles));
}

/** Exact nearest contact with an immutable authored surface, at most100k
 * finite nondegenerate triangles in local metres. A cached spatial index only
 * rejects empty bounds; final contact always uses the actual visible facets.
 * Mutable/malformed geometry returns null, preventing stale-cache collisions.
 * Rigid part motion transforms the ray, so no per-frame mesh rebuild is needed. */
export function skyLeviathanMeshSweep(from, to, triangles, radius = 0) {
  if (!finite(from) || !finite(to) || !Number.isFinite(radius) || radius < 0) return null;
  const index = meshIndex(triangles);
  if (!index) return null;
  let hit = null;
  function visit(node) {
    if (boundsEntry(from, to, node.bounds, radius, hit?.fraction ?? 1) === null) return;
    if (node.items) {
      for (const { triangle } of node.items) {
        const candidate = skyLeviathanTriangleSweep(from, to, triangle, radius);
        if (candidate && (!hit || candidate.fraction < hit.fraction)) hit = candidate;
      }
      return;
    }
    const ordered = [node.left, node.right].map((child) => ({
      child,
      entry: boundsEntry(from, to, child.bounds, radius, hit?.fraction ?? 1),
    }));
    ordered.sort((a, b) => (a.entry ?? Infinity) - (b.entry ?? Infinity));
    for (const item of ordered) if (item.entry !== null) visit(item.child);
  }
  visit(index);
  return hit;
}

/** Earliest double-sided segment/triangle contact in a single coordinate frame.
 * radius0 is an exact projectile ray; a positive metre radius sweeps a physical
 * sphere, including rounded edge/corner contact. Analytic face-plane and
 * edge/vertex quadratic roots prevent tunnelling without an iteration cap or
 * expanded rectangular approximation. Touching slides/departures are allowed. Invalid
 * data returns null. The returned point is the moving sphere centre/ray point. */
export function skyLeviathanTriangleSweep(from, to, triangle, radius = 0) {
  const bounds = geometry(triangle);
  if (!finite(from) || !finite(to) || !bounds || !Number.isFinite(radius) || radius < 0)
    return null;
  for (let index = 0; index < axes.length; index++) {
    const axis = axes[index];
    if (
      Math.min(from[axis], to[axis]) - radius > bounds[index][1] ||
      Math.max(from[axis], to[axis]) + radius < bounds[index][0]
    )
      return null;
  }
  const velocity = sub(to, from);
  const contact = (fraction) => ({
    fraction,
    point: add(from, velocity, fraction),
    triangleId: triangle.id,
  });
  if (radius === 0) {
    const edge1 = sub(triangle.b, triangle.a),
      edge2 = sub(triangle.c, triangle.a);
    const h = cross(velocity, edge2),
      determinant = dot(edge1, h);
    if (Math.abs(determinant) < 1e-12) return null;
    const inverse = 1 / determinant,
      offset = sub(from, triangle.a);
    const u = dot(offset, h) * inverse;
    if (u < 0 || u > 1) return null;
    const q = cross(offset, edge1),
      v = dot(velocity, q) * inverse;
    if (v < 0 || u + v > 1) return null;
    const fraction = dot(edge2, q) * inverse;
    return fraction >= 0 && fraction <= 1 ? contact(fraction) : null;
  }
  const separation = sub(from, nearest(from, triangle));
  const initialDistance = Math.hypot(separation.x, separation.y, separation.z);
  if (initialDistance < radius - 1e-8) return contact(0);
  if (initialDistance <= radius + 1e-8) {
    return dot(separation, velocity) < -1e-12 ? contact(0) : null;
  }
  const speed2 = dot(velocity, velocity);
  if (!(speed2 > 1e-20) || !Number.isFinite(speed2)) return null;
  let first = Infinity;
  const accept = (fraction) => {
    if (fraction >= 0 && fraction <= 1 && fraction < first) first = fraction;
  };
  const normal = cross(sub(triangle.b, triangle.a), sub(triangle.c, triangle.a));
  const length = Math.hypot(normal.x, normal.y, normal.z);
  for (const axis of axes) normal[axis] /= length;
  const planeOffset = dot(sub(from, triangle.a), normal),
    planeSpeed = dot(velocity, normal);
  if (Math.abs(planeSpeed) > 1e-12) {
    for (const sign of [-1, 1]) {
      const fraction = (sign * radius - planeOffset) / planeSpeed;
      if (fraction < 0 || fraction > 1) continue;
      const projected = add(add(from, velocity, fraction), normal, -sign * radius);
      const delta = sub(projected, nearest(projected, triangle));
      if (dot(delta, delta) < 1e-14) accept(fraction);
    }
  }
  const vertices = [triangle.a, triangle.b, triangle.c];
  for (let index = 0; index < 3; index++) {
    const start = vertices[index],
      end = vertices[(index + 1) % 3];
    const offset = sub(from, start),
      edge = sub(end, start);
    for (const fraction of roots(
      speed2,
      2 * dot(offset, velocity),
      dot(offset, offset) - radius ** 2,
    ))
      accept(fraction);
    const edge2 = dot(edge, edge),
      startAlong = dot(offset, edge) / edge2;
    const speedAlong = dot(velocity, edge) / edge2;
    const perpendicular = add(offset, edge, -startAlong);
    const perpendicularSpeed = add(velocity, edge, -speedAlong);
    for (const fraction of roots(
      dot(perpendicularSpeed, perpendicularSpeed),
      2 * dot(perpendicular, perpendicularSpeed),
      dot(perpendicular, perpendicular) - radius ** 2,
    )) {
      const along = startAlong + speedAlong * fraction;
      if (along >= 0 && along <= 1) accept(fraction);
    }
  }
  return Number.isFinite(first) ? contact(first) : null;
}

// Only inward crossings count; a double root merely grazes the surface. The
// stable quadratic form avoids losing the near root on long, shallow sweeps.
function roots(a, b, c) {
  if (a < 1e-20) return b < -1e-12 ? [-c / b] : [];
  const discriminant = b * b - 4 * a * c;
  if (discriminant <= 1e-14) return [];
  const q = -0.5 * (b + Math.sign(b || 1) * Math.sqrt(discriminant));
  return [q / a, c / q].sort((x, y) => x - y).slice(0, 1);
}
import { SKY_WORLD } from './86-sky-world-scale.mjs';
import { SKY_TERRAIN, skyTerrainHeight, skyTerrainMeshHeight } from './87-sky-terrain.mjs';
import { skyFlightFrame, skyFlightPointToWorld } from './92-sky-flight-frame.mjs';

/** Metre height of the actual supporting surface. The causeway uses its smooth
 * analytic profile; the wider fin footprint uses the rendered hillside's exact
 * triangles and Y origin. This prevents an analytic hill approximation from
 * accepting visibly buried fins. Travel is the encounter's frozen terrain basis. */
export function skyLeviathanGroundAt(x, z, travel) {
  return Math.abs(x) <= SKY_TERRAIN.roadHalfWidth
    ? skyTerrainHeight(x, z, travel)
    : SKY_WORLD.terrainY + skyTerrainMeshHeight(x, z, travel);
}

/** Lowest safe root Y for measured hull-local supports under the supplied rigid
 * orientation/XZ position. Invalid geometry/frame returns null. Supports must
 * be sampled from the final articulated mesh, not assumed central belly points;
 * this solver cannot certify mesh regions absent from that authored contract. */
export function skyLeviathanSupportHeight(frame, probes, travel) {
  const basis = skyFlightFrame(frame);
  if (!basis || !Array.isArray(probes) || !probes.length || !probes.every(finite)) return null;
  basis.origin.y = 0;
  let height = -Infinity;
  for (const probe of probes) {
    const world = skyFlightPointToWorld(basis, probe);
    height = Math.max(height, skyLeviathanGroundAt(world.x, world.z, travel) - world.y);
  }
  return Number.isFinite(height) ? height : null;
}
